import React, { useState } from 'react'
import { SiteLink } from '../../app-types'
import { createFindProsPath } from '../../routes/utils'
import { graphql, navigate, useStaticQuery } from 'gatsby'
import { Role } from '../../components/ConnectModuleForm'
import {
  trackConnectFormSubmissionError,
  trackFindGigsEvent,
  trackFindProsEvent,
} from '../../utils/analytics'
import { siteLinkToProps } from '../../utils/siteLinkToProps'
import { getClosestMarketToZip } from '../../gigproApi/getClosestMarketToZip'
import { getConnectModuleProsLink } from './getConnectModuleProsLink'
import { SkillGroup, SKILL_GROUPS } from '../../gigproApi/skillGroups'

export const isValidZip = (zip: string) => {
  // use the following regex to validate the zip code /(^\d{5}$)|(^\d{5}-\d{4}$)/
  return /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(zip)
}

/** API-sourced market cities for the zip lookup (see gatsby-node sourceNodes). */
interface ConnectMarketCity {
  id: string
  gigProID: number | null
  city: string
  market: { state: string }
}

export function useConnectModuleForm({
  params,
  notFoundRedirectLink,
}: {
  params?: string
  notFoundRedirectLink: SiteLink
}) {
  const { allMarketCity } = useStaticQuery<{
    allMarketCity: { nodes: ConnectMarketCity[] }
  }>(graphql`
    query ConnectModuleMarketCities {
      allMarketCity {
        nodes {
          id
          gigProID
          city
          market {
            state
          }
        }
      }
    }
  `)
  const allMarketCities = allMarketCity.nodes
  const [zip, setZip] = useState<string>('')
  const [role, setRole] = React.useState<Role>('Business')
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const [selectedSkillGroup, setSelectedSkillGroup] = useState<SkillGroup>(
    SKILL_GROUPS[0],
  )
  const isValid = isValidZip(zip) && selectedSkillGroup

  const handleSubmit = async () => {
    const value = { zip, skillGroup: selectedSkillGroup, role }

    if (!isValidZip(value.zip)) {
      trackConnectFormSubmissionError('Invalid Zip Code')
    }
    if (!isValidZip(value.zip) || !selectedSkillGroup) {
      setSubmitted(true)
      return
    }

    setLoading(true)

    let marketId: number | undefined

    try {
      marketId = await getClosestMarketToZip(value.zip)
    } catch (e) {
      console.error(e)
      trackConnectFormSubmissionError('Get Closest Market API Failed')
      setError('Oh no! Something went wrong. Please try again later.')
      setLoading(false)
      return
    } finally {
    }

    if (!marketId) {
      trackConnectFormSubmissionError('No Market Found At Zip')
      if (value.role === 'Business') {
        trackFindProsEvent({
          skill_id: value.skillGroup.closestGigproSkillGroupId,
          skill_name: value.skillGroup.id,
          postal_code: value.zip,
          market_exists: false,
        })
      } else if (value.role === 'Pro') {
        trackFindGigsEvent({
          skill_id: value.skillGroup.closestGigproSkillGroupId,
          skill_name: value.skillGroup.id,
          postal_code: value.zip,
          market_exists: false,
        })
      }

      const calculatedLink = siteLinkToProps(notFoundRedirectLink)

      // @ts-expect-error fixme
      const url = calculatedLink?.to ?? calculatedLink?.href

      if (!url || value.role === 'Pro') {
        trackConnectFormSubmissionError('Not In Market')
        setError('Not in your market at this time.')
        setLoading(false)
      } else {
        navigate(url, { state: { skillGroup: value.skillGroup.id } })
      }
      return
    }
    const marketCity = allMarketCities.find(
      (marketCity) => marketCity.gigProID === marketId,
    )

    if (!marketCity) {
      console.error('Could not find market city')
      trackConnectFormSubmissionError('Market City Not Found')
      setError('Oh no! Something went wrong. Please try again later.')
      setLoading(false)
      return
    }

    let path: string

    if (value.role === 'Business') {
      trackFindProsEvent({
        skill_id: value.skillGroup.closestGigproSkillGroupId,
        skill_name: value.skillGroup.id,
        postal_code: value.zip,
        market_exists: true,
        market_id: marketCity.id,
      })
      path =
        createFindProsPath(
          marketCity.market.state,
          marketCity,
          value.skillGroup.slug,
        ) + `${params ?? ''}`
      navigate(path)
    } else if (value.role === 'Pro') {
      trackFindGigsEvent({
        skill_id: value.skillGroup.closestGigproSkillGroupId,
        skill_name: value.skillGroup.id,
        postal_code: value.zip,
        market_exists: true,
        market_id: marketCity.id,
      })
      navigate(getConnectModuleProsLink(marketCity, value.skillGroup, params))
    }
  }

  return {
    zip,
    setZip,
    role,
    setRole,
    error,
    setError,
    loading,
    setLoading,
    submitted,
    setSubmitted,
    isValid,
    selectedSkillGroup,
    setSelectedSkillGroup,
    handleSubmit,
  }
}

export type useConnectModuleFormReturnType = ReturnType<
  typeof useConnectModuleForm
>
